import React from "react";
import { useAppContext } from "@qpr/client-api-base/context/AppCtxProvider";
import useLink from "@qpr/client-api-base/hooks/useLink";
import useHierarchy from "backend/hooks/elements/useHierarchy";
import Page from "design/pages/Page";
import useElement from "backend/hooks/elements/useElement";
import PageContainer from "design/layouts/PageContainer";
import { DivisionCard } from "design/components/cards/DivisionCard";
import MeasureGridArea from "design/components/grids/MeasureGridArea";
import useBySymbol from "backend/hooks/elements/useBySymbol";
import CascadeToolbar from "components/toolbars/CascadeToolbar";
import { useQuery } from "react-query";
import Query from "@qpr/client-api-base/querybuilder/Query";
import defaultAttributes from "backend/defaultAttributes";
import { ICardElementWithChildren } from "backend/model/model";
import { getFileApiURL } from "@qpr/client-api-base/utils/url-utils";
import usePeriodLevels from "backend/hooks/model/usePeriodLevels";
import { t } from "i18next";

const CascadePage = () => {
  const ctx = useAppContext();
  const link = useLink();
  const { data: theme } = useBySymbol("L0");
  const { data: periodlevels } = usePeriodLevels();
  const { data: element } = useElement(theme?.id);
  const {
    data,
    isLoading: isDataLoading,
    isError,
    isPreviousData,
    isRefetching,
  } = useHierarchy(theme?.id);

  const { data: topElements, isLoading: isLoadingTopElements } = useQuery(
    `topelements-${theme?.id}-${ctx.period}`,
    () => {
      return Query(data?.map((el) => el.id))
        .topelement()
        .attributes(...defaultAttributes(ctx.period, periodlevels!))
        .fetchValues<ICardElementWithChildren>()
        .then((list) => list.sort((a, b) => a.symbol.localeCompare(b.symbol)));
    },
    { enabled: !!data && !!periodlevels }
  );

  return (
    <Page
      isLoading={
        !topElements || !data || isDataLoading || isRefetching || isPreviousData
      }
      Toolbar={CascadeToolbar}
    >
      <PageContainer width="1000px">
        <MeasureGridArea>
          {data
            ?.sort((a, b) => a.symbol.localeCompare(b.symbol))
            .map((el, idx) => {
              const top = topElements?.find(
                (t) => t.scorecardsymbol === el.symbol
              );
              if (!top) {
                return null;
              }
              return (
                <DivisionCard
                  key={el.id}
                  topElement={top}
                  rangeColor={top.rangecolor}
                  rangeName={top.rangename}
                  cardTitle={t(`cascade.Division`)}
                  departmentIcon={getFileApiURL(`/icons/divisions/DEP_1.svg`)}
                  backgroundImage={getFileApiURL(
                    `/images/divisions/divisions.png`
                  )}
                  typenames={[
                    {
                      name: "Scorecard",
                      title: t("cascade.Departments"),
                    },
                  ]}
                  element={el}
                  onSelect={() => {
                    link({ page: "strategymap", meaId: el.topelement });
                  }}
                  onSelectChild={(item) => {
                    link({ page: "strategymap", meaId: item.topelement });
                  }}
                />
              );
            })}
        </MeasureGridArea>
      </PageContainer>
    </Page>
  );
};
export default CascadePage;
